/** @jsxImportSource react */
import { Route, Navigate } from 'react-router-dom'
import Layout from './components/common/Layout'
import ProtectedRoute from './components/common/ProtectedRoute'
import TenantRoute from './components/common/TenantRoute'
import AdminRoute from './components/common/AdminRoute'
import StandaloneShell from './components/common/StandaloneShell'
import LoginPage from './pages/LoginPage'
import SelectTenantPage from './pages/SelectTenantPage'
import InvitePage from './pages/InvitePage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import SsoCompletePage from './pages/SsoCompletePage'
import PublicFormPage from './pages/PublicFormPage'
import SignupPage from './pages/SignupPage'
import SignupVerifyPage from './pages/SignupVerifyPage'
import SignPage from './pages/SignPage'
import DocumentTemplatesPage from './pages/settings/DocumentTemplatesPage'
import EmailRepliesPage from './pages/settings/EmailRepliesPage'
import DashboardPage from './pages/DashboardPage'
import RecordListPage from './pages/records/RecordListPage'
import RecordEditPage from './pages/records/RecordEditPage'
import RecordImportPage from './pages/records/RecordImportPage'
import FormListPage from './pages/forms/FormListPage'
import FormBuilderPage from './pages/forms/FormBuilderPage'
import MembersPage from './pages/settings/MembersPage'
import AutomationsPage from './pages/settings/AutomationsPage'
import TemplatesPage from './pages/settings/TemplatesPage'
import RolesPage from './pages/settings/RolesPage'
import RoleEditPage from './pages/settings/RoleEditPage'
import AutomationEditPage from './pages/settings/AutomationEditPage'
import TenantsPage from './pages/platform/TenantsPage'
import TasksPage from './pages/TasksPage'
import CalendarPage from './pages/CalendarPage'
import ForecastPage from './pages/ForecastPage'
import CampaignsPage from './pages/campaigns/CampaignsPage'
import CampaignEditorPage from './pages/campaigns/CampaignEditorPage'
import UnsubscribePage from './pages/UnsubscribePage'
import ApprovalsPage from './pages/ApprovalsPage'
import ProcessSettingsPage from './pages/settings/ProcessSettingsPage'
import WhatsAppSettingsPage from './pages/settings/WhatsAppSettingsPage'
import ReportsPage from './pages/reports/ReportsPage'
import ReportBuilderPage from './pages/reports/ReportBuilderPage'
import OpenLinkPage from './pages/OpenLinkPage'
import PrivacyPage from './pages/settings/PrivacyPage'
import IntegrationsPage from './pages/settings/IntegrationsPage'
import EmailTemplatesPage from './pages/settings/EmailTemplatesPage'
import BillingPage from './pages/settings/BillingPage'
import PlatformBillingPage from './pages/platform/PlatformBillingPage'

/** Route tree for createBrowserRouter (data router: needed for useBlocker). */
const appRoutes = (
  <>
    <Route path="/login" element={<LoginPage />} />
    {/* public: the invite token is the credential */}
    <Route path="/invite/:token" element={<InvitePage />} />
    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
    {/* public: the one-time token (in the #fragment) is the credential */}
    <Route path="/reset-password" element={<ResetPasswordPage />} />
    <Route path="/sso/complete" element={<SsoCompletePage />} />
    {/* public web forms: anyone with the link, embeddable */}
    <Route path="/f/:key" element={<PublicFormPage />} />
    <Route path="/signup" element={<SignupPage />} />
    <Route path="/signup/verify" element={<SignupVerifyPage />} />
    <Route path="/sign" element={<SignPage />} />
    <Route path="/u/:token" element={<UnsubscribePage />} />
    {/* a tapped push notification: switch workspace if needed, then open the page */}
    <Route path="/open" element={<ProtectedRoute><OpenLinkPage /></ProtectedRoute>} />
    <Route
      path="/select-tenant"
      element={
        <ProtectedRoute>
          <SelectTenantPage />
        </ProtectedRoute>
      }
    />
    {/* outside TenantRoute: a platform admin may not belong to any tenant yet */}
    <Route
      path="/platform/tenants"
      element={
        <ProtectedRoute>
          <StandaloneShell title="Platform · Tenants">
            <TenantsPage />
          </StandaloneShell>
        </ProtectedRoute>
      }
    />
    <Route
      path="/platform/billing"
      element={
        <ProtectedRoute>
          <StandaloneShell title="Platform · Plans & billing">
            <PlatformBillingPage />
          </StandaloneShell>
        </ProtectedRoute>
      }
    />
    <Route
      path="/"
      element={
        <ProtectedRoute>
          <TenantRoute>
            <Layout />
          </TenantRoute>
        </ProtectedRoute>
      }
    >
      <Route index element={<DashboardPage />} />
    <Route path="dashboards/:dashboardId" element={<DashboardPage />} />
      <Route path="records/:entityType" element={<RecordListPage />} />
      <Route path="records/:entityType/new" element={<RecordEditPage />} />
    <Route path="records/:entityType/import" element={<AdminRoute><RecordImportPage /></AdminRoute>} />
      <Route path="records/:entityType/:recordId" element={<RecordEditPage />} />
      <Route path="forms" element={<AdminRoute><FormListPage /></AdminRoute>} />
      <Route path="forms/new" element={<AdminRoute><FormBuilderPage /></AdminRoute>} />
      <Route path="forms/:formId" element={<AdminRoute><FormBuilderPage /></AdminRoute>} />
      <Route path="tasks" element={<TasksPage />} />
      <Route path="calendar" element={<CalendarPage />} />
      <Route path="reports" element={<ReportsPage />} />
      <Route path="reports/new" element={<ReportBuilderPage />} />
      <Route path="reports/:reportId" element={<ReportBuilderPage />} />
      <Route path="forecast" element={<ForecastPage />} />
      <Route path="approvals" element={<ApprovalsPage />} />
      <Route path="settings/process" element={<AdminRoute><ProcessSettingsPage /></AdminRoute>} />
      <Route path="settings/whatsapp" element={<AdminRoute><WhatsAppSettingsPage /></AdminRoute>} />
      <Route path="campaigns" element={<AdminRoute><CampaignsPage /></AdminRoute>} />
      <Route path="campaigns/new" element={<AdminRoute><CampaignEditorPage /></AdminRoute>} />
      <Route path="campaigns/:campaignId" element={<AdminRoute><CampaignEditorPage /></AdminRoute>} />
      <Route path="settings/members" element={<MembersPage />} />
      <Route path="settings/privacy" element={<AdminRoute><PrivacyPage /></AdminRoute>} />
      <Route path="settings/integrations" element={<AdminRoute><IntegrationsPage /></AdminRoute>} />
      <Route path="settings/email-templates" element={<AdminRoute><EmailTemplatesPage /></AdminRoute>} />
      <Route path="settings/document-templates" element={<AdminRoute><DocumentTemplatesPage /></AdminRoute>} />
      <Route path="settings/email-replies" element={<EmailRepliesPage />} />
      <Route path="settings/billing" element={<AdminRoute><BillingPage /></AdminRoute>} />
      <Route path="settings/roles" element={<AdminRoute><RolesPage /></AdminRoute>} />
      <Route path="settings/roles/new" element={<AdminRoute><RoleEditPage /></AdminRoute>} />
      <Route path="settings/roles/:roleId" element={<AdminRoute><RoleEditPage /></AdminRoute>} />
      <Route path="settings/templates" element={<AdminRoute><TemplatesPage /></AdminRoute>} />
      <Route path="settings/automations" element={<AdminRoute><AutomationsPage /></AdminRoute>} />
      <Route path="settings/automations/new" element={<AdminRoute><AutomationEditPage /></AdminRoute>} />
      <Route path="settings/automations/:ruleId" element={<AdminRoute><AutomationEditPage /></AdminRoute>} />
    </Route>
    <Route path="*" element={<Navigate to="/" replace />} />
  </>
)


export default appRoutes
